// Types mirroring the Boort backend API contract (see specs/boort-spec.md §4, §5).

export type BookStatus = "queued" | "processing" | "completed" | "failed" | "canceled";

export type Stage =
  | "VALIDATE"
  | "FETCH_METADATA"
  | "FETCH_TRANSCRIPT"
  | "PREPROCESS"
  | "GUARDRAILS"
  | "OUTLINE"
  | "WRITE"
  | "VERIFY"
  | "ASSEMBLE"
  | "SUMMARIZE"
  | "RENDER"
  | "COMPLETED";

export type FidelityNoteType = "omission" | "distortion" | "addition" | "misattribution";
export type FidelitySeverity = "critical" | "minor";

export interface FidelityNote {
  chapter: number;
  chapter_title: string;
  type: FidelityNoteType;
  severity: FidelitySeverity;
  source_quote: string;
  detail: string;
}

export type GuardrailSeverity = "block" | "flag" | "info";
export type GuardrailSource = "pattern" | "density" | "screen" | "external";

export interface GuardrailNote {
  category: string;
  severity: GuardrailSeverity;
  detail: string;
  source_quote: string | null;
  sentence: number | null;
  source: GuardrailSource;
}

export interface Book {
  id: string;
  category_id: string | null;
  video_id: string;
  url: string;
  status: BookStatus;
  stage: Stage | null;
  error_code: string | null;
  error_message: string | null;
  error_hint: string | null;
  video_title: string | null;
  channel: string | null;
  duration_s: number | null;
  upload_date: string | null;
  video_lang: string | null;
  thumbnail_url: string | null;
  book_title: string | null;
  subtitle: string | null;
  synopsis: string | null;
  language: string | null;
  chapters_total: number | null;
  chapters_done: number;
  word_count: number | null;
  reading_minutes: number | null;
  fidelity_notes: FidelityNote[];
  guardrail_notes: GuardrailNote[];
  provider: string | null;
  model_writer: string | null;
  model_verifier: string | null;
  tokens_input: number;
  tokens_output: number;
  cost_usd: number;
  cost_is_estimate: boolean;
  has_epub: boolean;
  has_pdf: boolean;
  has_md: boolean;
  has_summary: boolean;
  cover_url: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

export interface Category {
  id: string;
  name: string;
  chat_prompt: string;
  book_count: number;
}

export type EventLevel = "info" | "warn" | "error";

export interface EventRow {
  id: number;
  ts: string;
  stage: string | null;
  level: EventLevel;
  message: string;
}

export interface CreateBookOptions {
  force?: boolean;
  paper_size?: string;
  /** "auto" (follow the video) or a language code; unset = the global setting. */
  language?: string;
  precise_mode?: boolean;
  /** Target reading time in minutes — makes a condensed paper. Unset = full length. */
  reading_minutes?: number;
  /** Owner override for a CONTENT_BLOCKED verdict ("Build anyway"). */
  ignore_guardrails?: boolean;
}

export interface CreateBookRequest {
  url: string;
  options?: CreateBookOptions;
}

export interface BooksListResponse {
  books: Book[];
  total: number;
}

export interface EventsResponse {
  events: EventRow[];
}

export type PaperSize = "A5" | "A4" | "Letter";

export interface ProviderInfo {
  name: string;
  configured: boolean;
  detail: string;
}

export type GuardrailsMode = "block" | "flag" | "off";

export interface Settings {
  paper_size: PaperSize;
  language: string;
  precise_mode: boolean;
  max_video_hours: number;
  max_transcript_chars: number;
  guardrails_mode: GuardrailsMode;
}

export interface SettingsUpdateRequest {
  paper_size?: PaperSize;
  language?: string;
  precise_mode?: boolean;
  max_video_hours?: number;
  max_transcript_chars?: number;
  guardrails_mode?: GuardrailsMode;
}

export interface HealthResponse {
  status: string;
  worker_heartbeat_age_s: number | null;
  queue_depth: number;
  provider: ProviderInfo;
}

export interface AuthStatus {
  required: boolean;
  authenticated: boolean;
  /** "firebase" = in-app sign-in; "cloudflare" = Google at the Cloudflare edge; "password"; "none". */
  mode: "firebase" | "cloudflare" | "password" | "none";
  /** Google account email when signed in through Cloudflare Access. */
  email: string | null;
  /** False when a valid account is not on the beta allowlist. */
  invited: boolean;
}

export type OutputFormat = "epub" | "pdf" | "md";

// ---------------------------------------------------------------------------
// Chat
// ---------------------------------------------------------------------------

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

export interface ChatSource {
  book_id: string;
  title: string;
}

export interface ChatResponse {
  reply: string;
  sources: ChatSource[];
  scoped_books: number;
  tokens_input: number;
  tokens_output: number;
  cost_usd: number;
  cost_is_estimate: boolean;
}

// Error envelope returned by the API for any 4xx/5xx response.
export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    hint: string | null;
  };
}

export class ApiError extends Error {
  code: string;
  hint: string | null;
  status: number;

  constructor(status: number, code: string, message: string, hint: string | null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.hint = hint;
  }
}
